import React, { Component } from 'react';
import 'theme-builder/dist/index.css';
import PageHeader from '../components/PageHeader';
import ThemeViewer from '../components/ThemeViewer';
import __ from '../utils/translator';

import ThemeStoreActions from '../flux/ThemeStoreActions';
import ThemeStore from '../flux/ThemeStore';
import ThemeStatusTypes from '../flux/ThemeStatusTypes';
import ThemeExerciseCompleted from '../components/ThemeExerciseCompleted';
import PageLoadingOverlayStore from '../flux/PageLoadingOverlayStore';
import getSearchQueryFromQueryString
  from '../utils/getSearchQueryFromQueryString';
import ThemeLabelContainer
  from '../components/theme-card/ThemeLabelContainer';
import PageTitle from '../components/PageTitle';
import Header from '../components/organisms/Header';
import themeResponsiveProperty
  from '../components/responsive/themeResponsiveProperty';
import { withTheme } from 'styled-components';
import GlossaryStoreActions from "../flux/GlossaryStoreActions";

function isTheSameThemeUrl(prevProps, props) {
  return (
    prevProps.match.params.collectionId === props.match.params.collectionId &&
    prevProps.match.params.themeId === props.match.params.themeId
  )
}

const defaultThemeProps = {
  theme: null,
  collectionId: null,
  collectionName: '-',
  themeName: '-',
  themeCover: null,
  themeCompleted: false,
  chapters: [],
  contents: [],
  glossary: [],
  related: [],
  nextTheme: null,
};

function mapThemeProps(theme) {
  if (theme === null) {
    return defaultThemeProps;
  }

  return {
    theme: theme,
    collectionId: theme.collectionId,
    collectionName: theme.collectionName,
    themeName: theme.name,
    themeCover: theme.cover,
    themeCompleted: theme.status === ThemeStatusTypes.THEME_STATUS_COMPLETED,
    themeConfidential: theme.confidential,
    chapters: theme.chapters,
    contents: theme.contents,
    glossary: theme.glossary,
    related: theme.related,
    nextTheme: theme.nextTheme === undefined ? null : theme.nextTheme,
  }
}

class Theme extends Component {
  state = {
    isPageLoading: PageLoadingOverlayStore.getState(),
    themeDidLoad: false,
    ...defaultThemeProps,
  };

  subscriptions = [];
  /** @type {null|Function} */
  onExerciseCompletePromiseResolver = null;

  componentDidMount() {
    ThemeStoreActions.find(
      this.props.match.params.collectionId, this.props.match.params.themeId
    );

    this.subscriptions.push(ThemeStore.addListener(this.handleThemeStoreChange));

    this.subscriptions.push(PageLoadingOverlayStore.addListener(() => {
      this.setState({ isPageLoading: PageLoadingOverlayStore.getState() });
    }));
  }

  componentDidUpdate(prevProps) {
    if ( ! isTheSameThemeUrl(prevProps, this.props)) {
      ThemeStoreActions.find(
        this.props.match.params.collectionId, this.props.match.params.themeId,
      );
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(subscription => subscription.remove());
  }

  handleThemeStoreChange = () => {
    const theme = ThemeStore.getState();

    if (
      theme !== null &&
      theme.status === ThemeStatusTypes.THEME_STATUS_COMPLETED &&
      this.onExerciseCompletePromiseResolver !== null
    ) {
      this.onExerciseCompletePromiseResolver();
    }

    this.setState({
      themeDidLoad: theme !== null,
      ...mapThemeProps(theme),
    });
  };

  onExerciseComplete = () => {
    ThemeStoreActions.complete(this.state.theme.collectionId, this.state.theme.id);

    return new Promise(resolve => {
      this.onExerciseCompletePromiseResolver = resolve;
    });
  };

  onChapterEnter = (index) => {
    if (this.state.chapters[index - 1] !== undefined) {
      ThemeStoreActions.chapterEnter(this.state.chapters[index - 1]._id, this.state.theme.id);
    }
  }

  onGlossaryTermShow = glossaryId => {
    GlossaryStoreActions.viewGlossaryTerm(glossaryId);
  }

  render() {
    return (
      <div>
        <Header actions={themeResponsiveProperty(['back', 'search', 'glossary'], undefined)(this.props)} />

        {this.state.themeDidLoad && (
          <PageTitle withTracking title={`Seasonal / ${this.state.collectionName} / ${this.state.themeName}`} />
        )}

        <PageHeader
          style={{ position: 'relative' }}
          cover={this.state.themeCover}
          crumbs={[__('Home'), __('Seasonal'), this.state.collectionName, this.state.themeName]}
          title={this.state.themeName}>
          {this.state.theme !== null && (
            <ThemeLabelContainer theme={this.state.theme} />
          )}
        </PageHeader>

        { ! this.state.isPageLoading && (
          <ThemeViewer
            highlightWord={getSearchQueryFromQueryString(this.props.location.search)}
            chapters={this.state.chapters}
            contents={this.state.contents}
            glossary={this.state.glossary}
            onExerciseComplete={this.onExerciseComplete}
            exerciseCompleted={this.state.themeCompleted}
            onChapterEnter={this.onChapterEnter}
            onGlossaryTermShow={this.onGlossaryTermShow}
          />
        )}

        {this.state.themeCompleted && (
          <ThemeExerciseCompleted
            theme={this.state.theme}
            nextTheme={this.state.nextTheme}
            related={this.state.related}
            exploreLink={`/seasonal/${this.state.collectionId}/themes`}
            exploreCallToAction={__("Find out all the collection themes")} />
        )}
      </div>
    );
  }
}

export default withTheme(Theme);
